import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { RestaurantSEO } from '../components/SEO';
import { 
  Star, Clock, MapPin, Phone, Plus, ShoppingCart, Calendar, ArrowLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { RestaurantReviews, RatingDisplay } from '../components/Reviews';

export default function RestaurantDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { addItem, cart, getItemCount, getTotal } = useCart();
  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetchRestaurant();
    fetchMenu();
  }, [id]);

  const fetchRestaurant = async () => {
    try {
      const response = await api.get(`/restaurants/${id}`);
      setRestaurant(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurant:', error);
      toast.error('Restaurant not found');
      navigate('/restaurants');
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await api.get(`/restaurants/${id}/menu`);
      setMenuItems(response.data);
    } catch (error) {
      console.error('Failed to fetch menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (item) => {
    if (!restaurant) return;
    addItem(item, restaurant);
    toast.success(`${item.name} added to cart`);
  };

  const getCategories = () => {
    const cats = ['all'];
    for (let i = 0; i < menuItems.length; i++) {
      if (!cats.includes(menuItems[i].category)) {
        cats.push(menuItems[i].category);
      }
    }
    return cats;
  };

  const getFilteredItems = () => {
    if (activeCategory === 'all') return menuItems;
    const filtered = [];
    for (let i = 0; i < menuItems.length; i++) {
      if (menuItems[i].category === activeCategory) {
        filtered.push(menuItems[i]);
      }
    }
    return filtered;
  };

  if (loading || !restaurant) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-64 bg-slate-200 rounded-2xl mb-6"></div>
            <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-slate-200 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  const categories = getCategories();
  const filteredItems = getFilteredItems();

  const renderCategoryButtons = () => {
    const buttons = [];
    for (let i = 0; i < categories.length; i++) {
      const category = categories[i];
      buttons.push(
        <Button
          key={category}
          variant={activeCategory === category ? 'default' : 'outline'}
          className={`rounded-full whitespace-nowrap ${activeCategory === category ? 'btn-primary' : ''}`}
          onClick={() => setActiveCategory(category)}
          data-testid={`category-${category}`}
        >
          {category === 'all' ? 'All Items' : category}
        </Button>
      );
    }
    return buttons;
  };

  const renderMenuItems = () => {
    if (filteredItems.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-slate-500">No menu items available</p>
        </div>
      );
    }

    const items = [];
    for (let i = 0; i < filteredItems.length; i++) {
      const item = filteredItems[i];
      items.push(
        <div
          key={item.id}
          className="flex gap-4 p-4 rounded-xl border border-slate-100 hover:border-orange-200 transition-colors"
          data-testid={`menu-item-${item.id}`}
        >
          {item.image_url && (
            <img
              src={item.image_url}
              alt={item.name}
              className="w-24 h-24 rounded-lg object-cover flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-slate-900">{item.name}</h3>
                {item.description && (
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{item.description}</p>
                )}
              </div>
              <span className="font-bold text-orange-500 text-lg">£{item.price.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center mt-3">
              <div className="flex gap-2">
                {item.dietary_info && item.dietary_info.length > 0 && (
                  <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                    {item.dietary_info[0]}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                className="btn-primary rounded-full"
                onClick={() => handleAddToCart(item)}
                disabled={!item.is_available}
                data-testid={`add-to-cart-${item.id}`}
              >
                {item.is_available ? (
                  <span className="flex items-center">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </span>
                ) : (
                  'Unavailable'
                )}
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return items;
  };

  const renderCartItems = () => {
    if (!cart.items.length || cart.restaurant?.id !== id) {
      return (
        <div className="text-center py-8">
          <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">{t('cart.empty')}</p>
          <p className="text-sm text-slate-400 mt-1">Add items from the menu</p>
        </div>
      );
    }

    const items = [];
    for (let i = 0; i < cart.items.length; i++) {
      const item = cart.items[i];
      items.push(
        <div key={item.id} className="flex justify-between items-center text-sm">
          <span className="text-slate-600">
            {item.quantity}x {item.name}
          </span>
          <span className="font-medium">£{(item.price * item.quantity).toFixed(2)}</span>
        </div>
      );
    }

    return (
      <div>
        <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
          {items}
        </div>
        <div className="border-t border-slate-100 pt-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">{t('cart.subtotal')}</span>
            <span>£{(getTotal() - (restaurant?.delivery_fee || 0)).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-500">{t('cart.deliveryFee')}</span>
            <span>£{restaurant?.delivery_fee?.toFixed(2) || '2.50'}</span>
          </div>
          <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-100">
            <span>{t('cart.total')}</span>
            <span className="text-orange-500">£{getTotal().toFixed(2)}</span>
          </div>
        </div>
        <Button 
          className="btn-primary w-full mt-4"
          onClick={() => navigate('/cart')}
          data-testid="view-cart-button"
        >
          <ShoppingCart className="w-5 h-5 mr-2" />
          View Cart ({getItemCount()})
        </Button>
      </div>
    );
  };

  return (
    <>
      <RestaurantSEO restaurant={restaurant} />
      <div className="min-h-screen bg-slate-50 pt-16">
        <div className="relative h-64 md:h-80">
          <img
            src={restaurant.image_url || 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200&h=400&fit=crop'}
            alt={restaurant.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
          <Button 
            variant="ghost" 
            className="absolute top-4 left-4 bg-white/90 hover:bg-white rounded-full"
            onClick={() => navigate(-1)}
            data-testid="back-button"
          >
          <ArrowLeft className="w-5 h-5 mr-2" /> Back
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative z-10">
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-testid="restaurant-name">
                {restaurant.name}
              </h1>
              <p className="text-slate-500 mb-3">{restaurant.cuisine_type?.join(', ') || 'Various Cuisines'}</p>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  <span className="font-medium">{restaurant.rating?.toFixed(1) || '4.5'}</span>
                </div>
                <div className="flex items-center gap-1 text-slate-500">
                  <Clock className="w-4 h-4" />
                  <span>25-35 min</span>
                </div>
                <div className="flex items-center gap-1 text-slate-500">
                  <MapPin className="w-4 h-4" />
                  <span>{restaurant.address}, {restaurant.city}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              {restaurant.delivery_enabled && (
                <span className="badge-success">Delivery Available</span>
              )}
              <Button 
                variant="outline" 
                className="rounded-full"
                onClick={() => navigate(`/book-table/${id}`)}
                data-testid="book-table-button"
              >
                <Calendar className="w-4 h-4 mr-2" /> Book Table
              </Button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mt-6 pt-6 border-t border-slate-100">
            <div>
              <span className="text-sm text-slate-500">Minimum Order</span>
              <p className="font-semibold text-slate-900">£{restaurant.minimum_order || 10}</p>
            </div>
            <div>
              <span className="text-sm text-slate-500">Delivery Fee</span>
              <p className="font-semibold text-slate-900">£{restaurant.delivery_fee || 2.50}</p>
            </div>
            <div>
              <span className="text-sm text-slate-500">Contact</span>
              <p className="font-semibold text-slate-900">{restaurant.phone}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">{t('restaurant.menu')}</h2>
              
              <div className="flex gap-2 overflow-x-auto pb-4 mb-4 scrollbar-hide">
                {renderCategoryButtons()}
              </div>

              <div className="space-y-4">
                {renderMenuItems()}
              </div>
            </div>

            {/* Reviews Section */}
            <div className="bg-white rounded-2xl shadow-sm p-6">
              <RestaurantReviews restaurantId={id} />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm p-6 sticky top-24">
              <h2 className="text-xl font-bold text-slate-900 mb-4">{t('cart.title')}</h2>
              {renderCartItems()}
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
}
